import { Link } from 'react-router-dom';
import { BadgeCheck, Crown, Fuel, Gauge, Cog, CalendarDays, User } from 'lucide-react';
import PriceDisplay from './PriceDisplay';
import { cardFrame, riseDelay, GOLD_BADGE } from './frames';

export const CAR_PLACEHOLDER = 'https://images.unsplash.com/photo-1583121274602-3e2820c69888?w=800&q=60';

const CONDITION = {
  new:       { ar: 'جديدة',   en: 'New',       cls: 'bg-emerald-500/90' },
  used:      { ar: 'مستعملة', en: 'Used',      cls: 'bg-[#1E9BD9]/90' },
  certified: { ar: 'معتمدة',  en: 'Certified', cls: 'bg-[#F59E0B]/90' },
};

const Spec = ({ icon: Icon, children, dark }) => (
  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-lg text-[11px] font-medium ${dark ? 'bg-white/5 text-slate-300' : 'bg-slate-100 text-slate-600'}`}>
    <Icon className="h-3 w-3 text-[#1E9BD9]" />
    {children}
  </span>
);

/** CarCard — Cyber-Luxe vehicle card: image with condition chip → name → spec chips → price/store. */
export default function CarCard({ item, isDarkMode, isAr, index = 0, testIdPrefix = 'car-card' }) {
  const img = (Array.isArray(item.images) && item.images[0]) || item.image || CAR_PLACEHOLDER;
  const name = [item.make, item.model].filter(Boolean).join(' ') || item.title || (isAr ? 'سيارة' : 'Car');
  const cond = CONDITION[item.condition];
  const isPersonal = item.is_personal || item.seller_type === 'personal';

  return (
    <Link
      to={`/car/${item.id}`}
      state={{ store: item.store, item }}
      className={`group relative block rounded-3xl overflow-hidden transition-all duration-300 hover:-translate-y-1 cgm-shimmer cgm-rise ${cardFrame(isDarkMode)}`}
      style={riseDelay(index)}
      data-testid={`${testIdPrefix}-${item.id}`}
    >
      <div className="relative aspect-[16/10] overflow-hidden">
        <img src={img} alt={name} onError={(e) => { e.currentTarget.src = CAR_PLACEHOLDER; }} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
        <div className={`absolute inset-0 bg-gradient-to-t ${isDarkMode ? 'from-[#181C2A] via-[#181C2A]/20' : 'from-black/40 via-transparent'} to-transparent`} />
        <div className="absolute top-3 start-3 flex items-center gap-1.5">
          {cond && <span className={`px-2.5 py-1 rounded-full text-[10px] font-bold text-white backdrop-blur ${cond.cls}`}>{isAr ? cond.ar : cond.en}</span>}
          {isPersonal && (
            <span className="inline-flex items-center gap-0.5 px-2 py-1 rounded-full text-[10px] font-bold bg-[#F5A962]/90 text-white backdrop-blur" data-testid={`personal-badge-${item.id}`}>
              <User className="h-2.5 w-2.5" />{isAr ? 'شخصي' : 'Personal'}
            </span>
          )}
        </div>
        <div className="absolute top-3 end-3 flex items-center gap-1.5">
          {item.featured && <span className={GOLD_BADGE}><Crown className="h-3 w-3" />{isAr ? 'مميز' : 'VIP'}</span>}
          {item.store?.verified && <span className="w-6 h-6 rounded-full bg-white/90 flex items-center justify-center"><BadgeCheck className="h-4 w-4 text-[#1E9BD9]" /></span>}
        </div>
        <div className="absolute inset-x-0 bottom-0 cgm-scanline opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
      </div>

      <div className="p-4 sm:p-5">
        <h3 className={`text-base sm:text-lg font-bold line-clamp-1 mb-2.5 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{name}</h3>
        <div className="flex flex-wrap gap-1.5 mb-3.5">
          {item.year && <Spec icon={CalendarDays} dark={isDarkMode}>{item.year}</Spec>}
          {item.mileage != null && <Spec icon={Gauge} dark={isDarkMode}>{Number(item.mileage).toLocaleString()} km</Spec>}
          {item.fuel_type && <Spec icon={Fuel} dark={isDarkMode}>{item.fuel_type}</Spec>}
          {item.transmission && <Spec icon={Cog} dark={isDarkMode}>{item.transmission}</Spec>}
        </div>
        <div className={`flex items-center justify-between gap-2 pt-3 border-t ${isDarkMode ? 'border-white/5' : 'border-slate-100'}`}>
          <PriceDisplay item={item} locale={isAr ? 'ar' : 'en'} size="sm" />
          <span className={`text-xs truncate max-w-[130px] ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>{item.store?.name || (isPersonal ? (isAr ? 'إعلان شخصي' : 'Personal ad') : '—')}</span>
        </div>
      </div>
    </Link>
  );
}
